import React, { useState, useEffect } from 'react';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../authConfig";
import AppCarousel from '../components/AppCarousel';
import { useParams } from 'react-router-dom';
const API_URL = process.env.REACT_APP_API_URL || "";

interface Category {
  id: number;
  name: string;
  status: boolean;
}

interface Feature {
  id: number;
  description: string;
  status: string;
  version: string;
}

interface SystemRequirements {
  framework: string;
  frameworkURL: string;
}

interface VersionFeatures {
  version: string;
  items: string[];
}

interface InputOutput {
  id: number;
  applicationId: number;
  ioType: string;
  description: string;
  status: boolean;
}

interface AppApiResponse {
  applicationType: 'Web'|'Desktop'
  id: number;
  name: string;
  description: string;
  status: boolean;
  version: string;
  categories: Category[];
  url: string;
  downloadCount: number;
  releaseDate: string;
  size: string;
  languages: string[];
  developedBy: string;
  releaseNotes: string[];
  systemRequirements: SystemRequirements;
  lastFeatures: VersionFeatures[];
  features: Feature[];
  inputOutputs: InputOutput[];
  sampleImages: string[];
}


interface AppData {
  input: string[];
  output: string[];
  lastAddedFeatures: string[];
  features: Feature[];
  releaseNotes: string[];
  systemRequirements: {
    text: string;
    link: string;
  };
  additionalInfo: {
    creator: string;
    creationDate: string;
    size: string;
    filesize: string;
    language: string;
    version: string;
    appType?: 'webapp' | 'desktop';
  };
  relatedResources: {
    name: string;
    link?: string;
  }[];
  screenshots?: string[];
}

const AppCenter: React.FC = () => {
  const { appname } = useParams<{ appname: string }>();
  const { instance, accounts } = useMsal();
  const [appData, setAppData] = useState<AppData | null>(null);
  const [apiResponse, setApiResponse] = useState<AppApiResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  


  // Función para transformar la respuesta de la API al formato que espera el componente
  const transformApiResponse = (data: AppApiResponse): AppData => {
    // Extraer inputs y outputs
    const inputs = data.inputOutputs
      .filter(io => io.ioType === 'Input')
      .map(io => io.description);
    
    const outputs = data.inputOutputs
      .filter(io => io.ioType === 'Output')
      .map(io => io.description);
    
    // Extraer last features (usamos el primer conjunto si existe)
    const lastFeatures = data.lastFeatures && data.lastFeatures.length > 0 
      ? data.lastFeatures[0].items 
      : [];

    // Creación de fecha formateada
    const releaseDate = new Date(data.releaseDate);
    const formattedDate = `${releaseDate.toLocaleString('default', { month: 'short' })} ${releaseDate.getDate()}, ${releaseDate.getFullYear()}`;

    return {
      input: inputs,
      output: outputs,
      lastAddedFeatures: lastFeatures,
      features: data.features,
      releaseNotes: data.releaseNotes,
      systemRequirements: {
        text: data.systemRequirements.framework,
        link: data.systemRequirements.frameworkURL
      },
      additionalInfo: {
        creator: data.developedBy,
        creationDate: formattedDate,
        size: data.size,
        filesize: data.size,
        language: data.languages.join(', '),
        version: data.version
      },
      relatedResources: [
        { name: 'Tutorials', link: '#' },
        { name: 'Documentation', link: data.url },
        { name: 'Support', link: '#' },
        { name: 'FAQs', link: '#' },
      ],
      screenshots: data.sampleImages
    };
  };

  useEffect(() => {
    const fetchAppData = async () => {
      if (!appname) {
        setError('App name is required');
        return;
      }
      
      try {
        setIsLoading(true);
        
        // Acquire token silently
        const response = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });

        const accessToken = response.accessToken;
  
        // Fetch app information using the appname from URL parameter
        const apiResponse = await fetch(`${API_URL}/engineering/appsender/info?appName=${encodeURIComponent(appname)}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
  
        if (!apiResponse.ok) {
          throw new Error(`HTTP error! Status: ${apiResponse.status}`);
        }
  
        const data = await apiResponse.json();
        setApiResponse(data);
        
        // Transformar la respuesta de la API al formato que espera el componente
        const transformedData = transformApiResponse(data);
        setAppData(transformedData);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching app data:', err);
        setError('Failed to load application data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
  
    if (accounts && accounts.length > 0) {
      fetchAppData();
    }
  }, [accounts, instance, appname]);

  return (
    <div className="min-h-screen bg-gray-50 font-roboto">
      {/* Header */}
      <div className="relative rounded-lg overflow-hidden mb-6" style={{ maxHeight: '300px' }}>
        <img src="/headerBlue.svg" alt="Header background" className="absolute inset-0 w-full h-full object-cover" />
        <div className="relative z-10 p-12 pl-16 text-left">
          <h2 className="text-5xl font-roboto text-white text-left">{apiResponse?.name}</h2>
          {apiResponse?.categories && (
            <div className="flex flex-wrap gap-2 mt-4">
              {apiResponse.categories.map((cat, index) => (
                <span key={index} className="bg-white text-[#1D5176] px-2 py-0.5 rounded-md text-xs font-roboto inline-block">
                  {cat.name}
                </span>
              ))}
              {apiResponse?.downloadCount && (
                <span className="text-white text-sm ml-2 inline-block">{apiResponse.downloadCount} downloads</span>
              )}
            </div>
          )}
        
          <div className="relative group">
          <p className="text-white text-base mt-4 max-w-xl text-left font-roboto line-clamp-2" 
            title={apiResponse?.description}>
            {apiResponse?.description}
          </p>
          </div>
          
          {apiResponse && (
            <a href={apiResponse.url} target="_blank" rel="noopener noreferrer">
              <button className="bg-[#1D5176] text-[#F1F7FD] px-6 py-2 rounded font-roboto hover:bg-[#2A6690] transition-colors mt-8">
                {apiResponse.applicationType === 'Desktop' ? 'Download Installer': "Go to Application"}
              </button>
            </a>
          )}
        </div>
      </div>
      
      {/* Loading/Error states */}
      {isLoading && (
        <div className="text-center py-8">
          <p>Loading application data...</p>
        </div>
      )}
      
      {error && (
        <div className="text-center py-8">
          <p className="text-red-500">{error}</p>
        </div>
      )}
      
      {/* Updated Carousel Component */}
      {appData && (
        <div className="container mx-auto px-6 mb-6">
          <AppCarousel screenshots={appData.screenshots || []} />
        </div>
      )}
      
      {appData && (
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* Left Column (3/4 width) */}
            <div className="col-span-3 space-y-6">
              {/* Input/Output Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-[#1D5176]">
                  <h3 className="bg-[#1D5176] text-white px-4 py-2 font-medium">Input</h3>
                  <div className="p-4">
                    <ul className="list-disc pl-5 space-y-2">
                      {appData.input.map((item: string, index: number) => (
                        <li key={index} className="text-sm">{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-[#1D5176]">
                  <h3 className="bg-[#1D5176] text-white px-4 py-2 font-medium">Output</h3>
                  <div className="p-4">
                    <ul className="list-disc pl-5 space-y-2">
                      {appData.output.map((item: string, index: number) => (
                        <li key={index} className="text-sm">{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Last Added Features */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-[#1D5176]">
                <h3 className="bg-[#1D5176] text-white px-4 py-2 font-medium">Last added features</h3>
                <div className="p-4">
                  <p className="font-medium text-sm">V{appData.additionalInfo.version}</p>
                  <div className="ml-4 mt-2 space-y-2">
                    {appData.lastAddedFeatures.map((item: string, index: number) => (
                      <div key={index} className="flex gap-2 text-sm">
                        <span>{index + 1}.</span>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Features Table */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-[#1D5176]">
                <h3 className="bg-[#1D5176] text-white px-4 py-2 font-medium text-center">Features</h3>
                <div className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <tbody>
                        {appData.features.map((feature: Feature, index: number) => (
                          <tr key={index} className="border-b border-gray-200 last:border-b-0">
                            <td className="py-3 px-4 text-center text-gray-600 w-12">{feature.id}</td>
                            <td className="py-3 px-4 text-left pl-10">{feature.description}</td>
                            <td className="py-3 px-4 whitespace-nowrap text-right w-32">
                              <span className={
                                feature.status === "Included" ? "text-green-600" : 
                                feature.status === "In Queue" ? "text-yellow-600" : 
                                feature.status === "Needs Discussion" ? "text-red-600" : ""
                              }>
                                {feature.status}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right text-yellow-700 w-20">{feature.version}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Column (1/4 width - Sidebar) */}
            <div className="space-y-6">
              {/* Release Notes Card */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-[#1D5176]">
                <h3 className="bg-[#1D5176] text-white px-4 py-2 font-medium">Release Notes</h3>
                <div className="p-4">
                  <ul className="text-sm list-disc pl-5 space-y-2">
                    {appData.releaseNotes.map((note: string, index: number) => (
                      <li key={index}>{note}</li>
                    ))}
                  </ul>
                </div>
              </div>
              
              {/* System Requirements Card */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-[#1D5176]">
                <h3 className="bg-[#1D5176] text-white px-4 py-2 font-medium">System requirements</h3>
                <div className="p-4">
                  <p className="text-sm mb-2">{appData.systemRequirements.text}</p>
                  <a href={appData.systemRequirements.link} className="text-sm text-blue-600 hover:underline">
                    Go to Official Website
                  </a>
                </div>
              </div>
              
              {/* Additional Information Card */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-[#1D5176]">
                <h3 className="bg-[#1D5176] text-white px-4 py-2 font-medium">Additional Information</h3>
                <div className="p-4">
                  <div className="text-sm">
                    <div className="grid grid-cols-2 gap-4 py-2 border-b border-gray-100">
                      <div className="flex items-start">
                        <div className="mr-2 text-gray-400 mt-1">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                            <circle cx="12" cy="7" r="4"></circle>
                          </svg>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs font-light text-left">Developed by</p>
                          <p className="font-medium text-left">{appData.additionalInfo.creator}</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="mr-2 text-gray-400 mt-1">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="16" y1="2" x2="16" y2="6"></line>
                            <line x1="8" y1="2" x2="8" y2="6"></line>
                            <line x1="3" y1="10" x2="21" y2="10"></line>
                          </svg>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs font-light text-left">Release Date</p>
                          <p className="font-medium text-left">{appData.additionalInfo.creationDate}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 py-2 border-b border-gray-100">
                      <div className="flex items-start">
                        <div className="mr-2 text-gray-400 mt-1">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <rect x="7" y="7" width="3" height="9"></rect>
                            <rect x="14" y="7" width="3" height="5"></rect>
                          </svg>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs font-light text-left">Category</p>
                          <p className="font-medium text-left">{appData.additionalInfo.size}</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="mr-2 text-gray-400 mt-1">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="7 10 12 15 17 10"></polyline>
                            <line x1="12" y1="15" x2="12" y2="3"></line>
                          </svg>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs font-light text-left">Download Size</p>
                          <p className="font-medium text-left">{appData.additionalInfo.filesize}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 py-2 border-b-0">
                      <div className="flex items-start">
                        <div className="mr-2 text-gray-400 mt-1">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                            <line x1="12" y1="17" x2="12.01" y2="17"></line>
                          </svg>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs font-light text-left">Languages</p>
                          <p className="font-medium text-left">{appData.additionalInfo.language}</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="mr-2 text-gray-400 mt-1">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12 6 12 12 16 14"></polyline>
                          </svg>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs font-light text-left">Release Version</p>
                          <p className="font-medium text-left">{appData.additionalInfo.version}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Related Resources Card */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-[#1D5176]">
                <h3 className="bg-[#1D5176] text-white px-4 py-2 font-medium">Related resources</h3>
                <div className="p-4">
                  <div className="space-y-1">
                    {appData.relatedResources.map((resource: {name: string, link?: string}, index: number) => (
                      <div key={index} className="flex justify-between items-center text-sm py-2 border-b border-gray-100 last:border-b-0">
                        {resource.link ? (
                          <a href={resource.link} target="_blank" rel="noopener noreferrer">
                            <span>{resource.name}</span>
                          </a>
                        ) : (
                          <span>{resource.name}</span>
                        )}
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppCenter;